import { BorderRight, Widgets } from "@mui/icons-material";
import React from "react";
import { useEffect, useRef, useState } from "react";

const logos = [
  { src: "/logos/gym1.png", alt: "NASM" },
  { src: "/logos/gym2.png", alt: "ACE" },
  { src: "/logos/gym3.png", alt: "Precision Nutrition" },
  { src: "/logos/gym4.png", alt: "Anytime Fitness" },
  { src: "/logos/gym5.png", alt: "LA Fitness" },
  { src: "/logos/gym6.png", alt: "Snap Fitness" },
  { src: "/logos/gym7.png", alt: "Snap Fitness" },
  { src: "/logos/gym8.png", alt: "Snap Fitness" },
];

export default function LogoMarquee({
  isMobile = false,
  speed = isMobile ? 160 : 160, // px/sec
  gap = 32,
}: {
  speed?: number;
  gap?: number;
  isMobile?: boolean;
}) {
  // duplicate the list so it loops seamlessly
  const doubled = [
    ...logos,
    ...logos,
    ...logos,
    ...logos,
    ...logos,
    ...logos,
    ...logos,
    ...logos,
    ...logos,
  ];

  // Compute animation duration from track width at runtime
  const trackRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(30);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    // Estimate width to keep speed roughly constant
    if (!trackRef.current) return;
    const totalWidth = Array.from(trackRef.current.children).reduce(
      (sum, el) => sum + (el as HTMLElement).offsetWidth + gap,
      0
    );
    // time = distance / speed
    const seconds = Math.max(10, Math.round(totalWidth / speed));
    setDuration(seconds);
  }, [logos.length, speed, gap]);

  return (
    <div
      style={styles.marqueeWrap as React.CSSProperties}
      aria-label="Trusted by"
    >
      {/* tiny CSS keyframes for smooth infinite scroll */}
      <style>{`
          @keyframes avro-marquee {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>

      <div style={styles.marqueeViewport as React.CSSProperties}>
        <div
          ref={trackRef}
          style={{
            ...styles.marqueeTrack(duration, paused, gap),
            // duplicate content visually to allow seamless loop
            width: "200%",
          }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          role="list"
          aria-label="Logo carousel"
        >
          {doubled.map((l, i) => (
            <div
              key={i}
              style={styles.logoItem as React.CSSProperties}
              role="listitem"
            >
              <img
                src={l.src}
                style={styles.logoImg as React.CSSProperties}
                loading="lazy"
                height={28}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  marqueeWrap: {
    marginTop: 14,
    width: "50vh",
  },
  marqueeViewport: {
    position: "relative",
    overflow: "hidden",
    maskImage:
      "linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0))",
    WebkitMaskImage:
      "linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0))",
  },
  marqueeTrack: (
    duration: number,
    paused: boolean,
    gap: number
  ): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap,
    animation: `avro-marquee ${duration}s linear infinite`,
    animationPlayState: paused ? "paused" : "running",
    willChange: "transform",
  }),
  logoItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "10px 18px",
    borderRadius: 12,
    // background: "rgba(255,255,255,0.06)",
    // border: "1px solid rgba(255,255,255,0.12)",
    minHeight: 48,
  },
  logoImg: {
    filter: "grayscale(100%) contrast(1.05) brightness(0.95)",
    opacity: 0.9,
    height: 36,
    width: "auto",
    borderRadius: 60,
  },
  logoText: {
    color: "rgba(255,255,255,0.85)",
    fontWeight: 700,
    letterSpacing: 0.4,
    fontSize: 14,
  },
};
